import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, LineChart, Line, CartesianGrid, YAxis,
} from "recharts";
import { Fire, FileText, CalendarCheck, CalendarBlank, NotePencil, Timer, Airplane, Users, ClipboardText, Circle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function StatCard({ label, value, icon: Icon, tint, delay }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="relative p-5 border-border rounded-2xl overflow-hidden animate-in-up hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
          <p className="mt-2 font-heading text-3xl font-medium text-foreground font-mono-num">{value}</p>
        </div>
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon size={22} weight="fill" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [tasks, setTasks] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
      const t = await api.get("/tasks");
      setTasks(t.data);
      if (user?.role === "admin") {
        const a = await api.get("/admin/stats");
        setAdminStats(a.data);
      }
    } catch (e) {
      toast.error("Failed to load dashboard");
    }
  };

  const toggleTask = async (task) => {
    const next = task.status === "done" ? "pending" : "done";
    try {
      await api.put(`/tasks/${task.id}/status`, { status: next });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: next } : t)));
    } catch (e) { toast.error("Could not update task"); }
  };

  useEffect(() => { load(); }, []);

  if (!stats) return <div className="h-64 flex items-center justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Overview</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-normal tracking-tight mt-1">
            Hello, <span className="gradient-text">{user?.name?.split(" ")[0] || "there"}</span>
          </h1>
        </div>
        <Button data-testid="submit-report-cta" onClick={() => navigate("/submit")}
          className="rounded-full brand-gradient text-white border-0 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
          <NotePencil size={18} className="mr-1.5" weight="regular" /> Submit Today's Report
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Today" value={stats.today_count} icon={CalendarCheck} tint="bg-secondary text-primary" delay={0} />
        <StatCard label="This Week" value={stats.week_count} icon={CalendarBlank} tint="bg-secondary text-primary" delay={60} />
        <StatCard label="This Month" value={stats.month_count} icon={FileText} tint="bg-secondary text-primary" delay={120} />
        <StatCard label="Streak" value={`${stats.streak}d`} icon={Fire} tint="bg-[hsl(12_40%_94%)] text-[hsl(12_40%_45%)]" delay={180} />
      </div>

      {tasks.length > 0 && (
        <Card className="p-6 rounded-2xl border-border animate-in-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-medium tracking-tight flex items-center gap-2"><ClipboardText size={20} className="text-primary" /> My Assigned Tasks</h3>
            <span className="text-xs font-mono-num px-2.5 py-1 rounded-full bg-secondary text-primary">{tasks.filter((t) => t.status !== "done").length} pending</span>
          </div>
          <div className="space-y-2">
            {tasks.map((t) => (
              <div key={t.id} data-testid={`task-${t.id}`}
                className={`flex items-start gap-3 rounded-xl border p-3 transition-colors duration-200 ${t.status === "done" ? "bg-secondary/40 border-transparent" : "bg-white border-border"}`}>
                <button data-testid={`task-toggle-${t.id}`} onClick={() => toggleTask(t)} className="mt-0.5 shrink-0">
                  {t.status === "done"
                    ? <CheckCircle size={22} weight="fill" className="text-primary" />
                    : <Circle size={22} className="text-muted-foreground hover:text-primary transition-colors" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</p>
                  {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                    <span>Assigned by {t.assigned_by}</span>
                    {t.due_date && <span className="font-mono-num">Due {t.due_date}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 rounded-md border-border animate-in-up">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-lg font-medium tracking-tight">Last 7 Days</h3>
            <span className="text-xs text-muted-foreground">{stats.total_reports} total reports</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.weekly_trend}>
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} stroke="#94a3b8" />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Bar dataKey="count" fill="hsl(248 30% 38%)" radius={[4, 4, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 rounded-md border-border animate-in-up">
          <h3 className="font-heading text-lg font-medium tracking-tight mb-6">6-Month Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={stats.monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} stroke="#94a3b8" />
              <YAxis tickLine={false} axisLine={false} fontSize={12} stroke="#94a3b8" allowDecimals={false} width={24} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Line type="monotone" dataKey="count" stroke="hsl(12 35% 52%)" strokeWidth={2} dot={{ r: 3, fill: "hsl(12 35% 52%)" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-5 rounded-2xl border-border flex items-center gap-4 hover:shadow-md transition-shadow duration-200">
          <div className="h-11 w-11 rounded-xl bg-secondary text-primary flex items-center justify-center"><Timer size={22} weight="regular" /></div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Attendance Today</p>
            <p className="text-sm font-medium mt-0.5">
              {stats.attendance_today?.check_in
                ? (stats.attendance_today?.check_out ? "Checked out" : "Checked in")
                : "Not checked in"}
            </p>
          </div>
        </Card>
        <Card className="p-5 rounded-2xl border-border flex items-center gap-4 hover:shadow-md transition-shadow duration-200">
          <div className="h-11 w-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center"><Airplane size={22} weight="fill" /></div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pending Leaves</p>
            <p className="text-sm font-medium mt-0.5 font-mono-num">{stats.pending_leaves}</p>
          </div>
        </Card>
        {adminStats && (
          <Card className="p-5 rounded-2xl border-border flex items-center gap-4 hover:shadow-md transition-shadow duration-200">
            <div className="h-11 w-11 rounded-xl bg-secondary text-primary flex items-center justify-center"><Users size={22} weight="regular" /></div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Employees</p>
              <p className="text-sm font-medium mt-0.5 font-mono-num text-foreground">{adminStats.total_employees} · {adminStats.reports_today} reported today</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
